/**
 * User Management Store
 *
 * Zustand store that centralises all UI state for the User Management page.
 * Scoped components (InvitesTab, UsersTab, modals, filter drawers) consume
 * this store instead of relying on local `useState`.
 *
 * **State Decision**: Multi-step flow / multiple related states → Zustand Store
 *
 * @module features/users/store/userManagement
 */
import { create } from 'zustand';

import type {
  BusinessAccountUser,
  InviteFilters,
  InviteItem,
  UserFilters,
} from '@/features/users/types/users.types';
import { DEFAULT_PAGE_SIZE } from '@/features/users/constants/pagination';

// ============================================================================
// Constants
// ============================================================================

/** Semantic keys for the User Management tabs. */
export const TAB_KEYS = {
  INVITES: 'invites',
  USERS: 'users',
} as const;

/** Union type derived from `TAB_KEYS` values. */
export type TabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS];

// ============================================================================
// State Interface
// ============================================================================

interface UserManagementState {
  /** Currently active tab key */
  activeTab: TabKey;

  // ── Modal visibility ────────────────────────────────────────────
  inviteModalOpen: boolean;
  cancelInviteModalOpen: boolean;
  resendInviteModalOpen: boolean;
  editProfileModalOpen: boolean;
  deleteModalOpen: boolean;
  profilesInfoModalOpen: boolean;
  /** The invite targeted by the cancel / resend modals */
  selectedInvite: InviteItem | null;
  /** The user targeted by the edit / delete modals */
  selectedUser: BusinessAccountUser | null;

  // ── Invite tab ──────────────────────────────────────────────────
  invitePage: number;
  invitePageSize: number;
  selectedRowKeys: React.Key[];
  inviteFiltersOpen: boolean;
  appliedInviteFilters: InviteFilters;
  draftInviteFilters: InviteFilters;

  // ── User tab ────────────────────────────────────────────────────
  usersPage: number;
  usersPageSize: number;
  userFiltersOpen: boolean;
  appliedUserFilters: UserFilters;
  draftUserFilters: UserFilters;
}

// ============================================================================
// Actions Interface
// ============================================================================

interface UserManagementActions {
  // Tab
  setActiveTab: (tab: TabKey) => void;

  // Modals
  openInviteModal: () => void;
  closeInviteModal: () => void;
  openCancelInviteModal: (invite: InviteItem) => void;
  closeCancelInviteModal: () => void;
  openResendInviteModal: (invite: InviteItem) => void;
  closeResendInviteModal: () => void;
  openEditProfileModal: (user: BusinessAccountUser) => void;
  closeEditProfileModal: () => void;
  openDeleteModal: (user: BusinessAccountUser) => void;
  closeDeleteModal: () => void;
  openProfilesInfoModal: () => void;
  closeProfilesInfoModal: () => void;

  // Invite tab – pagination & selection
  setInvitePage: (page: number) => void;
  setInvitePageSize: (size: number) => void;
  setSelectedRowKeys: (keys: React.Key[]) => void;

  // Invite tab – filters
  openInviteFilters: () => void;
  closeInviteFilters: () => void;
  applyInviteFilters: () => void;
  clearInviteFilters: () => void;
  setDraftInviteFilters: (filters: InviteFilters) => void;

  // User tab – pagination
  setUsersPage: (page: number) => void;
  setUsersPageSize: (size: number) => void;

  // User tab – filters
  openUserFilters: () => void;
  closeUserFilters: () => void;
  applyUserFilters: () => void;
  clearUserFilters: () => void;
  setDraftUserFilters: (filters: UserFilters) => void;

  /** Reset entire store to initial values */
  reset: () => void;
}

// ============================================================================
// Store Type
// ============================================================================

export interface UserManagementStore
  extends UserManagementState, UserManagementActions {}

// ============================================================================
// Initial State
// ============================================================================

const initialState: UserManagementState = {
  activeTab: TAB_KEYS.INVITES,

  inviteModalOpen: false,
  cancelInviteModalOpen: false,
  resendInviteModalOpen: false,
  editProfileModalOpen: false,
  deleteModalOpen: false,
  profilesInfoModalOpen: false,
  selectedInvite: null,
  selectedUser: null,

  invitePage: 0,
  invitePageSize: DEFAULT_PAGE_SIZE,
  selectedRowKeys: [],
  inviteFiltersOpen: false,
  appliedInviteFilters: {},
  draftInviteFilters: {},

  usersPage: 0,
  usersPageSize: DEFAULT_PAGE_SIZE,
  userFiltersOpen: false,
  appliedUserFilters: {},
  draftUserFilters: {},
};

// ============================================================================
// Store
// ============================================================================

export const useUserManagementStore = create<UserManagementStore>()(
  (set, get) => ({
    ...initialState,

    // ── Tab ──────────────────────────────────────────────────────────
    setActiveTab: (tab) => set({ activeTab: tab, selectedRowKeys: [] }),

    // ── Modals ───────────────────────────────────────────────────────
    openInviteModal: () => set({ inviteModalOpen: true }),
    closeInviteModal: () => set({ inviteModalOpen: false }),

    openCancelInviteModal: (invite) =>
      set({ cancelInviteModalOpen: true, selectedInvite: invite }),
    closeCancelInviteModal: () =>
      set({ cancelInviteModalOpen: false, selectedInvite: null }),

    openResendInviteModal: (invite) =>
      set({ resendInviteModalOpen: true, selectedInvite: invite }),
    closeResendInviteModal: () =>
      set({ resendInviteModalOpen: false, selectedInvite: null }),

    openEditProfileModal: (user) =>
      set({ editProfileModalOpen: true, selectedUser: user }),
    closeEditProfileModal: () =>
      set({ editProfileModalOpen: false, selectedUser: null }),

    openDeleteModal: (user) =>
      set({ deleteModalOpen: true, selectedUser: user }),
    closeDeleteModal: () => set({ deleteModalOpen: false, selectedUser: null }),

    openProfilesInfoModal: () => set({ profilesInfoModalOpen: true }),
    closeProfilesInfoModal: () => set({ profilesInfoModalOpen: false }),

    // ── Invite tab – pagination & selection ──────────────────────────
    setInvitePage: (page) => set({ invitePage: page }),
    setInvitePageSize: (size) => set({ invitePageSize: size }),
    setSelectedRowKeys: (keys) => set({ selectedRowKeys: keys }),

    // ── Invite tab – filters ─────────────────────────────────────────
    openInviteFilters: () =>
      set({
        inviteFiltersOpen: true,
        draftInviteFilters: get().appliedInviteFilters,
      }),
    closeInviteFilters: () =>
      set({
        inviteFiltersOpen: false,
        draftInviteFilters: get().appliedInviteFilters,
      }),
    applyInviteFilters: () =>
      set({
        appliedInviteFilters: get().draftInviteFilters,
        invitePage: 0,
        inviteFiltersOpen: false,
      }),
    clearInviteFilters: () =>
      set({
        appliedInviteFilters: {},
        draftInviteFilters: {},
        invitePage: 0,
      }),
    setDraftInviteFilters: (filters) => set({ draftInviteFilters: filters }),

    // ── User tab – pagination ────────────────────────────────────────
    setUsersPage: (page) => set({ usersPage: page }),
    setUsersPageSize: (size) => set({ usersPageSize: size }),

    // ── User tab – filters ───────────────────────────────────────────
    openUserFilters: () =>
      set({
        userFiltersOpen: true,
        draftUserFilters: get().appliedUserFilters,
      }),
    closeUserFilters: () =>
      set({
        userFiltersOpen: false,
        draftUserFilters: get().appliedUserFilters,
      }),
    applyUserFilters: () =>
      set({
        appliedUserFilters: get().draftUserFilters,
        usersPage: 0,
        userFiltersOpen: false,
      }),
    clearUserFilters: () =>
      set({
        appliedUserFilters: {},
        draftUserFilters: {},
        usersPage: 0,
      }),
    setDraftUserFilters: (filters) => set({ draftUserFilters: filters }),

    // ── Reset ────────────────────────────────────────────────────────
    reset: () => set(initialState),
  })
);
